import { authFetch } from '@/services/apiClient'
import type { InvoiceInfo, BillingInfo } from '@/types/invoiceInfo'
import { toBillingInfoPayload } from '@/types/invoiceInfo'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export type InhouseStatus = 'NEW' | 'CONTACTED' | 'QUOTED' | 'CLOSED'

export interface InhouseRequest {
  request_id: number
  request_number: string
  course_id: number
  contact_name: string
  phone: string
  email: string
  company_name: string | null
  trainee_count: number
  date_from: string
  date_to: string
  location: string
  note: string
  status: InhouseStatus
  unit_price: number | null    // ราคา/วัน — คำนวณอัตโนมัติตอนสร้างคำขอ (35,000 บาท/วัน)
  total_price: number | null   // unit_price × จำนวนวัน
  accepted_at: string | null   // ลูกค้ากดยอมรับใบเสนอราคาเมื่อไหร่ — null = ยังไม่ยอมรับ
  billing_info: BillingInfo | null   // ข้อมูลใบแจ้งหนี้ — มีค่าเมื่อติ๊กขอตอนกดยอมรับ
  created_date: string
  course: {
    course_id: number; course_number: string; title: string; slug: string
    // ผู้สอนของคอร์ส = ผู้เสนอราคา (ชื่อ + ลายเซ็นท้ายใบเสนอราคา) — ดึงจาก m_instructor
    instructor: { first_name: string; last_name: string; signature_url: string | null } | null
  } | null
}

export interface InhouseRequestInput {
  course_id: number
  contact_name: string
  phone: string
  company_name?: string
  trainee_count: number
  date_from: string
  date_to: string
  location: string
  note: string
}

export const INHOUSE_MAX_TRAINEES = 30

async function parseJson<T>(res: Response): Promise<ApiResponse<T>> {
  try {
    return await res.json()
  } catch {
    return { success: false, error: 'เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ' }
  }
}

export const createInhouseRequest = async (data: InhouseRequestInput): Promise<InhouseRequest> => {
  const res = await authFetch('/inhouse-requests', { method: 'POST', body: JSON.stringify(data) })
  const json = await parseJson<InhouseRequest>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ส่งคำขอไม่สำเร็จ')
  return json.data
}

export const getMyInhouseRequests = async (): Promise<InhouseRequest[]> => {
  const res = await authFetch('/my-inhouse-requests')
  const json = await parseJson<InhouseRequest[]>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'โหลดคำขอไม่สำเร็จ')
  return json.data
}

export const getInhouseRequest = async (id: number | string): Promise<InhouseRequest> => {
  const res = await authFetch(`/inhouse-requests/${id}`)
  const json = await parseJson<InhouseRequest>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ไม่พบคำขอ')
  return json.data
}

// ยอมรับใบเสนอราคา — "ส่งอีเมลแจ้งทีมงานสำเร็จ" คือเงื่อนไขเดียวที่ทำให้ถือว่ายอมรับแล้ว (ดู accept_request_service ฝั่ง backend)
// ส่งไม่สำเร็จ = error กลับมา ไม่มีอะไรถูกบันทึก กดยอมรับใหม่ได้เลย
export const acceptInhouseRequest = async (id: number | string, invoiceInfo?: InvoiceInfo): Promise<InhouseRequest> => {
  const res = await authFetch(`/inhouse-requests/${id}/accept`, {
    method: 'POST',
    body: JSON.stringify({ billing_info: invoiceInfo ? toBillingInfoPayload(invoiceInfo) : undefined }),
  })
  const json = await parseJson<InhouseRequest>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ยอมรับใบเสนอราคาไม่สำเร็จ')
  return json.data
}
